import { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Car } from 'lucide-react';
import { useVehicleImages, VehicleImage } from '@/hooks/useVehicleImages';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const AdminVehicleImages = () => {
  const { vehicleImages, loading, createVehicleImage, updateVehicleImage, deleteVehicleImage } = useVehicleImages();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<VehicleImage | null>(null);
  const [formData, setFormData] = useState({
    service_type: 'standard' as 'standard' | 'vip',
    min_passengers: 1,
    max_passengers: 4,
    image_url: '',
    vehicle_name: '',
    description: '',
    features: '',
    is_active: true,
  });

  const resetForm = () => {
    setFormData({
      service_type: 'standard',
      min_passengers: 1,
      max_passengers: 4,
      image_url: '',
      vehicle_name: '',
      description: '',
      features: '',
      is_active: true,
    });
    setEditingVehicle(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const vehicleData = {
      ...formData,
      features: formData.features.split(',').map(f => f.trim()).filter(f => f),
    };

    try {
      if (editingVehicle) {
        await updateVehicleImage(editingVehicle.id, vehicleData);
      } else {
        await createVehicleImage(vehicleData);
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving vehicle:', error);
    }
  };

  const handleEdit = (vehicle: VehicleImage) => {
    setEditingVehicle(vehicle);
    setFormData({
      service_type: vehicle.service_type,
      min_passengers: vehicle.min_passengers,
      max_passengers: vehicle.max_passengers,
      image_url: vehicle.image_url,
      vehicle_name: vehicle.vehicle_name,
      description: vehicle.description || '',
      features: vehicle.features.join(', '),
      is_active: vehicle.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar esta configuración de vehículo?')) {
      await deleteVehicleImage(id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Configuración de Vehículos</h1>
            <p className="text-muted-foreground">
              Gestiona las imágenes y configuraciones de vehículos por capacidad de pasajeros
            </p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="mr-2 h-4 w-4" />
                Agregar Vehículo
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>
                  {editingVehicle ? 'Editar Vehículo' : 'Agregar Nuevo Vehículo'}
                </DialogTitle>
                <DialogDescription>
                  Configura las imágenes y detalles del vehículo según la capacidad de pasajeros
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="service_type">Tipo de Servicio</Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(value: 'standard' | 'vip') => 
                        setFormData(prev => ({ ...prev, service_type: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard Service</SelectItem>
                        <SelectItem value="vip">VIP Service</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="vehicle_name">Nombre del Vehículo</Label>
                    <Input
                      id="vehicle_name"
                      value={formData.vehicle_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_name: e.target.value }))}
                      placeholder="ej: Sedan, SUV, Van"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min_passengers">Mínimo de Pasajeros</Label>
                    <Input
                      id="min_passengers"
                      type="number"
                      min="1"
                      value={formData.min_passengers}
                      onChange={(e) => setFormData(prev => ({ ...prev, min_passengers: parseInt(e.target.value) }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="max_passengers">Máximo de Pasajeros</Label>
                    <Input
                      id="max_passengers"
                      type="number"
                      min="1"
                      value={formData.max_passengers}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_passengers: parseInt(e.target.value) }))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image_url">URL de la Imagen</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="/src/assets/vehicle-image.jpg"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descripción del vehículo"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="features">Características (separadas por comas)</Label>
                  <Textarea
                    id="features"
                    value={formData.features}
                    onChange={(e) => setFormData(prev => ({ ...prev, features: e.target.value }))}
                    placeholder="Aire acondicionado, Conductor profesional, WiFi"
                    rows={2}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active">Activo</Label>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {editingVehicle ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Configuraciones de Vehículos
            </CardTitle>
            <CardDescription>
              Lista de todas las configuraciones de vehículos por capacidad de pasajeros
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-6">Cargando configuraciones...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Vehículo</TableHead>
                    <TableHead>Capacidad</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicleImages.map((vehicle) => (
                    <TableRow key={vehicle.id}>
                      <TableCell>
                        <Badge variant={vehicle.service_type === 'vip' ? 'default' : 'secondary'}>
                          {vehicle.service_type === 'vip' ? 'VIP' : 'Standard'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{vehicle.vehicle_name}</TableCell>
                      <TableCell>
                        {vehicle.min_passengers}-{vehicle.max_passengers} pax
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {vehicle.description}
                      </TableCell>
                      <TableCell>
                        <Badge variant={vehicle.is_active ? 'default' : 'destructive'}>
                          {vehicle.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(vehicle)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(vehicle.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminVehicleImages;